import BasicButton from "@/components/core/buttons/BasicButton";
import ConfirmModal from "@/components/core/modals/ConfirmModal";
import InfoModal from "@/components/core/modals/InfoModal";
import OrderModal from "@/components/core/modals/OrderModal";
import { authenticationStore } from "@/state/auth/AuthStore";
import { requestGetBalanceForEveryOffices } from "@/state/balance/BalanceEffects";
import { logoutDetailsStore } from "@/state/logoutDetails/LogoutDetailsStore";
import { LogoutDetailsDto } from "@/state/logoutDetails/dto/response/logout.details.mode.dto";
import { requestGetCertificateQcpInformation } from "@/state/qcpCertificate/QCPCertificateEffects";
import {
  requestCreateUserCertificateQcpRegistration,
  requestCreateUserCertificateQcpRegistrationResume,
} from "@/state/users/UserEffects";
import { AuthenticationContext, UserOffice } from "@/type/core/auth/auth.types";
import { Facturation, Office } from "@/type/core/offices/offices.types";
import { QcpCertificateInformation } from "@/type/core/qcp/qcp.types";
import {
  REVOKE_QCP_HELP_LINK,
  SOMME_QCP_CERTIFICATE_CREATION_IN_CENTS,
} from "@/utils/Constant";
import { StatusEnum } from "@/utils/enums/status.enum";
import DateHelper from "@/utils/helpers/date-helper";
import { OfficeHelper } from "@/utils/helpers/office-helper";
import SherlockHelper from "@/utils/helpers/sherlock-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import { Col, Row } from "antd";
import { useUnit } from "effector-react";
import HTMLReactParser from "html-react-parser";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OfficeList from "@views/core/extranet/menu/account/mySignature/OfficeList";

interface QCPCertificateProps {}

const QCPCertificate: FunctionComponent<QCPCertificateProps> = ({}) => {
  const { t, i18n } = useTranslation();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const logoutDetails = useUnit<LogoutDetailsDto | null>(logoutDetailsStore);
  const u2PersonalSpaceUrl: string = logoutDetails?.u2PersonalSpaceUrl ?? "";
  const userEmail = authStore?.user?.email;
  const userCertificatePhone = authStore?.user?.certificatePhone;

  const [selectedOffice, setSelectedOffice] = useState<Office>();

  const { officesToDisplay } = useOfficesToDisplay();

  const [qcp, setQcp] = useState<QcpCertificateInformation>();
  const [facturation, setFacturation] = useState<Facturation>();
  const [isOrderModalVisible, setIsOrderModalVisible] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [
    isRevokeQcpCertificateModalVisible,
    setIsRevokeQcpCertificateModalVisible,
  ] = useState<boolean>(false);

  const isQcpInvalid =
    qcp && qcp.notification && qcp.qcpCertificateStatus === StatusEnum.INVALID;
  const userOffices = officesToDisplay.filter((office) =>
    office.rights?.includes("SIGNATURE_COLLECTE_SIGNER"),
  );

  useEffect(() => {
    setIsLoading(true);
    void requestGetCertificateQcpInformation({});
    void requestGetBalanceForEveryOffices({});
  }, []);

  useEffect(() => {
    return requestGetCertificateQcpInformation.done.watch(({ result }) => {
      if (result.ok) {
        if (result.data) {
          setQcp(result.data);
        }
      } else {
        toastError(t("error.toast.certificatQCPGet"));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestGetBalanceForEveryOffices.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setFacturation(result.data);
      } else {
        toastError(t("error.toast.officesBalance"));
      }
      setIsLoading(false);
    });
  });

  const errorDate = () => {
    const date = qcp?.date;
    const formattedDate = DateHelper.qcpFormattedDate(date ?? "");

    if (qcp?.notification === "connexion") {
      return "";
    }

    return t("account.mySignature.certificate.qcp.invalidReason.errorDate", {
      date: formattedDate,
    });
  };

  const errorReasonText = () => {
    if (isQcpInvalid) {
      if (qcp.verificationErrors) {
        return qcp.verificationErrors;
      } else if (
        i18n.exists(
          `account.mySignature.certificate.qcp.invalidReason.${qcp.notification}`,
        )
      ) {
        return t(
          `account.mySignature.certificate.qcp.invalidReason.${qcp.notification}`,
        );
      } else {
        return qcp.notification;
      }
    } else {
      return "";
    }
  };

  const showQcpCertificateStatusExplanation = (): string => {
    switch (qcp && qcp.qcpCertificateStatus) {
      case StatusEnum.VALID:
        return i18n.t("account.mySignature.certificate.qcp.ownCertificate");
      case StatusEnum.PROGRESS:
      case StatusEnum.STARTED:
        return i18n.t("account.mySignature.certificate.qcp.started");
      default:
        return "";
    }
  };

  const handleCreateQcpCertificate = () => {
    if (OfficeHelper.officeHasEnoughBalance(selectedOffice, facturation)) {
      if (selectedOffice) {
        setIsLoading(true);
        requestCreateUserCertificateQcpRegistration({
          dto: {
            email: userEmail,
            phone: userCertificatePhone,
            officeId: selectedOffice?.id,
            shouldCheckPhone: true,
          },
        })
          .then((response) => {
            if (response.ok) {
              // To open in a new tab
              if (response.data?.url) {
                window.open(response.data.url, "_blank");
              }
              void requestGetCertificateQcpInformation({});
            } else {
              toastError(t("error.toast.certificatQCPCreate"));
            }
          })
          .catch(() => {
            toastError(t("error.toast.certificatQCPCreate"));
          })
          .finally(() => setIsLoading(false));
      }
    } else {
      setIsOrderModalVisible(true);
    }
  };

  const handleResumeQcpCertificateCreation = () => {
    if (qcp?.officeId) {
      setIsLoading(true);
      requestCreateUserCertificateQcpRegistrationResume({
        dto: {
          email: userEmail,
          phone: userCertificatePhone,
          officeId: qcp.officeId,
          shouldCheckPhone: true,
        },
      })
        .then((response) => {
          if (response.ok) {
            if (response.data?.url) {
              window.open(response.data.url, "_blank");
            }
            void requestGetCertificateQcpInformation({});
          } else {
            toastError(t("error.toast.certificatQCPCreateRetry"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.certificatQCPCreateRetry"));
        })
        .finally(() => setIsLoading(false));
    }
  };

  const setupAndOpenOrderModal = (office: UserOffice) => {
    setSelectedOffice(office);
    setIsOrderModalVisible(true);
  };

  const handleConfirmRevokeQcpCertificate = () => {
    // if (!certificateDegradedMode) {
    window.open(u2PersonalSpaceUrl, "_blank");
    setIsRevokeQcpCertificateModalVisible(false);
    // } else {
    //   this.toggleRevokeQcpCertificateModal();
    // }
  };

  return (
    <div>
      <div className="mt-5">
        <Row>
          <Col>
            <strong className="text-primary">
              {i18n.t("account.mySignature.certificate.qcp.title")}
            </strong>
            <InfoModal className={"ml-4"} value="qcpCertificate" info />
          </Col>
        </Row>
        {qcp?.isQcpBlocked ? (
          <Row className={"mt-3"}>
            <Col className="white-space-pre-line">
              {i18n.t("account.mySignature.certificate.qcp.qcpBlocked")}
            </Col>
          </Row>
        ) : !qcp ||
          !qcp.qcpCertificateStatus ||
          (qcp.qcpCertificateStatus != StatusEnum.VALID &&
            qcp.qcpCertificateStatus != StatusEnum.STARTED &&
            qcp.qcpCertificateStatus != StatusEnum.PROGRESS) ? (
          <div>
            {isQcpInvalid && (
              <div>
                <Row className={"mt-16"}>
                  <Col className="white-space-pre-line color-danger">
                    {errorDate()}
                  </Col>
                </Row>
                <Row className={"mt-8"}>
                  <Col className="white-space-pre-line color-danger">
                    {errorReasonText()}
                  </Col>
                </Row>
              </div>
            )}
            <Row className={"mt-16"}>
              <Col className="white-space-pre-line">
                {i18n.t("account.mySignature.certificate.qcp.unavailable", {
                  cost: SOMME_QCP_CERTIFICATE_CREATION_IN_CENTS / 100,
                })}
              </Col>
            </Row>
            {userOffices.length > 0 && (
              <OfficeList
                userOffices={userOffices}
                facturation={facturation}
                selectedOffice={selectedOffice}
                setSelectedOffice={setSelectedOffice}
                setupAndOpenOrderModal={setupAndOpenOrderModal}
              />
            )}
            <Row className={"d-flex justify-content-center mt-16 mb-16"}>
              <Col>
                {userOffices.length > 0 ? (
                  <BasicButton
                    disabled={
                      isLoading ||
                      !(
                        OfficeHelper.officeHasEnoughBalance(
                          selectedOffice,
                          facturation,
                        ) === "enoughBalance"
                      )
                    }
                    onClick={(e) => {
                      setIsLoading(true);
                      e.preventDefault();
                      handleCreateQcpCertificate();
                    }}
                    isLoading={isLoading}
                    text={t(
                      "account.mySignature.certificate.qcp.askForNewCertificate",
                    )}
                  />
                ) : (
                  i18n.t(
                    "account.mySignature.certificate.qcp.noOfficeWithEnoughBalance",
                  )
                )}
              </Col>
            </Row>
          </div>
        ) : (
          <div>
            <Row className={"pt-16"}>
              <Col>{showQcpCertificateStatusExplanation()}</Col>
            </Row>
            {(qcp.qcpCertificateStatus === StatusEnum.STARTED ||
              qcp.qcpCertificateStatus === StatusEnum.PROGRESS) && (
              <Col className={"d-flex justify-content-center pt-16"}>
                <BasicButton
                  disabled={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleResumeQcpCertificateCreation();
                  }}
                  text={i18n.t(
                    "account.mySignature.certificate.qcp.continueCertificateCreation",
                  )}
                />
              </Col>
            )}
            {qcp.qcpCertificateStatus === StatusEnum.VALID && (
              <Row className="mt-8 d-flex justify-content-right align-items-center flex-row-reverse">
                <Col className="d-flex justify-content-right align-items-center flex-row-reverse">
                  <InfoModal className={"ml-4"} value="revokeQcpCertificate" />
                  <a
                    id={"revokeCertificateButton"}
                    className={"pt-2 pb-2"}
                    onClick={() => setIsRevokeQcpCertificateModalVisible(true)}
                    rel="noreferrer"
                  >
                    {i18n.t("account.mySignature.certificate.qcp.revoke")}
                  </a>
                </Col>
              </Row>
            )}
          </div>
        )}
      </div>
      {selectedOffice && selectedOffice.billing && facturation && (
        <OrderModal
          title={t("modals.orderModal.title")}
          visible={isOrderModalVisible}
          onCancel={() => setIsOrderModalVisible(false)}
          officeBalance={facturation?.officesBalance.find(
            (facture) => facture.officeId === selectedOffice.id,
          )}
          billing={selectedOffice.billing}
          onSubmit={(tarif) => {
            location.href = SherlockHelper.buildOrderUrl(
              selectedOffice.id,
              tarif,
            );
          }}
          defaultTarif={SOMME_QCP_CERTIFICATE_CREATION_IN_CENTS / 100}
        />
      )}
      <ConfirmModal
        title={t("account.mySignature.certificate.qcp.modal.revoke.title")}
        visible={isRevokeQcpCertificateModalVisible}
        content={HTMLReactParser(
          t("account.mySignature.certificate.qcp.modal.revoke.body", {
            link: REVOKE_QCP_HELP_LINK,
          }),
        )}
        onOk={handleConfirmRevokeQcpCertificate}
        onCancel={() => setIsRevokeQcpCertificateModalVisible(false)}
        okText={t(
          "account.mySignature.certificate.qcp.modal.revoke.yesActionLabel",
        )}
        cancelText={t(
          "account.mySignature.certificate.qcp.modal.revoke.noActionLabel",
        )}
      />
    </div>
  );
};

export default QCPCertificate;
